import { Spinner } from "@bestowinc/kindred-ds";
import type {
  ErrorModuleComponent,
  FlowMeta,
  ModuleRef,
  TinkertoyErrorInfo,
} from "@bestowinc/tinkertoy-core";
import { useEffect, useRef, useState } from "@bestowinc/tinkertoy-shell-deps/react";
import { Helmet } from "react-helmet";
import { isRouteErrorResponse, useRouteError } from "react-router";

import {
  FAILURE_EVENT_TAGS,
  isModuleLoadError,
  markFailureReported,
  reportFailureOnce,
  reportShellBoundaryFailure,
  resolveErrorCode,
  resolveFailureError,
  resolveFailureMessage,
} from "../error-telemetry";
import type { LoadedModule } from "../module-loader";
import { useModuleLoader } from "../module-loader/module-loader-context";
import { readSerializedShellConfig } from "../runtime-config/read-shell-config";
import { ShellLoaderError } from "./shell-loader-error";

export type { TinkertoyErrorInfo };

export function ShellErrorBoundary() {
  const error = useRouteError();
  const { status, message, code, errorView, errorContext, flowMeta } =
    narrowError(error);

  const reportedRef = useRef(false);
  useEffect(() => {
    if (reportedRef.current) {
      return;
    }
    reportedRef.current = true;
    reportShellBoundaryFailure(error, {
      tag: FAILURE_EVENT_TAGS.shellBoundary,
      errorCode: resolveErrorCode(error),
      message: resolveFailureMessage(error),
      moduleId: errorContext?.moduleId,
    });
  }, [error, errorContext?.moduleId]);

  return (
    <>
      <ShellDocumentTitle flowMeta={flowMeta} />
      {errorView ? (
        <ErrorViewHost
          errorView={errorView}
          status={status}
          message={message}
          code={code}
          errorType={errorContext?.errorType ?? code}
          moduleId={errorContext?.moduleId}
        />
      ) : (
        <InlineFallback status={status} message={message} />
      )}
    </>
  );
}

function ShellDocumentTitle({ flowMeta }: { flowMeta?: FlowMeta }) {
  const title =
    flowMeta?.title ?? readSerializedShellConfig()?.defaultDocumentTitle;
  if (!title) {
    return null;
  }

  return (
    <Helmet>
      <title>{title}</title>
    </Helmet>
  );
}

interface NarrowedError {
  status: number;
  message: string;
  code: string;
  errorView?: ModuleRef;
  errorContext?: { errorType: string; moduleId?: string };
  flowMeta?: FlowMeta;
}

function narrowError(error: unknown): NarrowedError {
  if (ShellLoaderError.isInstance(error)) {
    return {
      status: error.status,
      message: error.message,
      code: error.code,
      errorView: error.errorView,
      errorContext: error.errorContext,
      flowMeta: error.flowMeta,
    };
  }
  if (isRouteErrorResponse(error)) {
    return {
      status: error.status,
      message:
        typeof error.data === "string"
          ? error.data
          : (error.statusText ?? "An unexpected error occurred."),
      code: "route_error",
    };
  }
  if (isModuleLoadError(error)) {
    return {
      status: 500,
      message: resolveFailureMessage(error),
      code: error.code,
      errorContext: {
        errorType: "module_load_failure",
        moduleId: error.moduleId,
      },
    };
  }
  if (error instanceof Error) {
    return {
      status: 500,
      message: error.message,
      code: "unknown",
    };
  }
  return {
    status: 500,
    message: "An unexpected error occurred.",
    code: "unknown",
  };
}

interface ErrorViewHostProps {
  errorView: ModuleRef;
  status: number;
  message: string;
  code: string;
  errorType: string;
  moduleId?: string;
}

function ErrorViewHost({
  errorView,
  status,
  message,
  code,
  errorType,
  moduleId,
}: ErrorViewHostProps) {
  const loadState = useErrorViewModule(errorView);

  if (loadState.kind === "loading") {
    return <ErrorViewLoading />;
  }

  if (loadState.kind === "error") {
    return <InlineFallback status={status} message={message} />;
  }

  const ErrorComponent =
    loadState.module.definition.component as ErrorModuleComponent<
      Record<string, unknown>,
      unknown
    >;

  const error: TinkertoyErrorInfo = {
    type: errorType,
    code,
    status,
    message,
    ...(moduleId ? { moduleId } : {}),
  };

  return (
    <ErrorComponent
      props={errorView.props}
      error={error}
      data={loadState.moduleData}
    />
  );
}

type ModuleLoadState =
  | { kind: "loading" }
  | { kind: "loaded"; module: LoadedModule; moduleData: unknown }
  | { kind: "error"; error: unknown };

function useErrorViewModule(errorView: ModuleRef): ModuleLoadState {
  const loader = useModuleLoader();
  const [state, setState] = useState<ModuleLoadState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    loader
      .loadModule(errorView.moduleId, errorView.minVersion, {
        allowErrorModule: true,
      })
      .then((module) => {
        if (!cancelled) {
          // Error-path dataFetcher wiring is tracked in FEP2-4183. Until then,
          // error modules must declare empty Data; `{}` matches Record<string, never>.
          const moduleData = {};
          if (
            process.env["NODE_ENV"] !== "production" &&
            "dataFetcher" in module.definition &&
            module.definition.dataFetcher != null
          ) {
            console.warn(
              `[ShellErrorBoundary] Error module "${errorView.moduleId}" declares a dataFetcher but error-path data loading is not implemented yet (FEP2-4183). Passing empty data.`,
            );
          }
          setState({ kind: "loaded", module, moduleData });
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          if (isModuleLoadError(err)) {
            const failureError = resolveFailureError(
              err,
              `Failed to load error view module "${errorView.moduleId}".`,
            );
            reportFailureOnce(failureError, {
              tag: FAILURE_EVENT_TAGS.errorViewLoad,
              errorCode: err.code,
              message: failureError.message,
              error: failureError,
              moduleId: err.moduleId,
              moduleVersion: err.version,
            });
            markFailureReported(err);
          }
          setState({ kind: "error", error: err });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [loader, errorView.moduleId, errorView.minVersion]);

  return state;
}

function InlineFallback({ status, message }: { status: number; message: string }) {
  return (
    <div data-testid="shell-error-boundary" role="alert">
      <h1>{status}</h1>
      <p>{message}</p>
    </div>
  );
}

function ErrorViewLoading() {
  return (
    <div
      data-testid="shell-error-boundary-loading"
      role="status"
      aria-live="polite"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem",
      }}
    >
      <Spinner size="large" />
    </div>
  );
}
